import { FC, useCallback, useEffect, useRef, useState } from "react";

import { FormattedMessage, useIntl } from "react-intl";
import { endOfYear, format, getDaysInMonth, getTime, Locale, setDate } from "date-fns";

import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff, ArrowBack, Restore, FileDownloadOutlined } from "@mui/icons-material";
import { enUS, ruRU } from "@mui/x-data-grid";
import {
  DataGridPremium,
  GridCellParams,
  GridColDef,
  GridColumnGroupHeaderParams,
  GridColumnGroupingModel,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridRowParams,
  GridToolbarContainer,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from "@mui/x-data-grid-premium";

import { EnabledLanguages } from "@framework/constants";
import { Precision, RangeType, useAsu } from "@framework/provider-asu";
import { useUser } from "@framework/provider-user";
import { useApiCall } from "@framework/react-hooks";
import { UserVersion } from "@framework/types";

import { useDownloadReport, useGetTooltip } from "./api-hooks";
import { getVisualCommentTypeSymbol } from "./comment-types";
import {
  capitalize,
  getSelectedItemType,
  SELECTED_ITEM_TYPE,
  toISOStringJournal,
  formatDateHumanReadable,
} from "../../../../utils";
import {
  getDates,
  getJournalHourColumnDate,
  getJournalHourColumnField,
  getJournalPointKeysInResponseOrder,
  getPrecision,
  parseJournalCalendarDate,
} from "./utils";
import { saveBlob } from "../../../../utils/save-file";
import {
  mergeRecorderTrains,
  mergeRecorderPoints,
  mergeRecorderVisual,
  filterJournalTrainsToWindow,
  filterJournalPointsToWindow,
  filterJournalVisualToWindow,
  applyRecorderVisualSnapshot,
} from "./recorder";
import {
  fetchJournalSnapshot,
  fetchJournalVisual,
  getJournalSnapshotTrigger,
  getJournalVisualTransition,
  shouldFetchJournalVisual,
} from "./journal-requests";
import {
  getJournalContextKey,
  getJournalSelectedItemKey,
  mergeJournalPage,
  mergeJournalSnapshot,
  mergeJournalVisualResult,
} from "./journal-results";

import {
  IResponseTrain,
  IResponsePoint,
  IPrepareData,
  IJournalVisualResult,
  IRequestJournal,
  IRequestJournalVisual,
  IResponseVisualTrain,
  IResponseVisualPoint,
  JournalRecorderVisualItem,
} from "./interfaces";

import { InfoDialogButton } from "../../../../components/common/buttons/info-dialog";
import { InformationTable } from "../../../../components/common/tables/information-table";
import { StyledAlarmCell } from "../../../../components/common/styled/alarm-cell";
import { StyledButton } from "../../../../components/common/styled/button";

export type { TCommentTypeSymbol } from "./comment-types";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const { ru, enGB: en } = require("date-fns/locale");

const locales: { [key: string]: Locale } = { ru, en };

const journalCoerceIso = (ts: string | Date): string => (typeof ts === "string" ? ts : ts.toISOString());
const STATION_JOURNAL_PAGE_SIZE = 50;
const TRAIN_JOURNAL_PAGE_SIZE = 500;

export const Journal = () => {
  const user = useUser<any>();
  const hasAdvancedFeaturesAccess =
    user.profile.version === UserVersion.ADVANCED || user.profile.version === UserVersion.PROFESSIONAL;

  const {
    getAllNestedTrains,
    getTrain,
    selectTrain,
    selectStation,
    selectedItem,
    startTimestamp,
    endTimestamp,
    updatePrecision,
    updateQS,
    isLoading: asuLoading,
    isVisualControlOpen,
    setIsVisualControlOpen,
    userSelectedRangeType,
    shouldReloadJournal,
    setShouldReloadJournal,
    setIsJournalNavigation,
    setManualRangeType,
    journalTimeRangeHistory,
    setJournalTimeRangeHistory,
    setSkipNextJournalReload,
    setJustRestoredRange,
    updateTimestampsFakeUTC,
    dbTz,
    recorderTickId,
    recorderWindowStart,
    recorderWindowEnd,
    recorderModeInterval,
    userSelectedRecorderModePeriod,
  } = useAsu();

  const apiRef = useGridApiRef();
  const isRecorderMode = userSelectedRangeType === RangeType.RECORDER_MODE;

  const journalRecorderAccumRef = useRef<{
    trainsJson: IResponseTrain[];
    pointsJson: IResponsePoint[];
    visualJson: JournalRecorderVisualItem[];
  } | null>(null);
  const journalRecorderPendingVisualDeltasRef = useRef<JournalRecorderVisualItem[]>([]);
  const journalRecorderFetchKeyRef = useRef<string>("");
  const journalRecorderHasFullJournalRef = useRef<boolean>(false);
  const journalRecorderRequestVersionRef = useRef<number>(0);
  const { formatMessage, locale } = useIntl();
  const {
    downloadReportByTrainId,
    isReportLoading,
    open: isReportDialogOpen,
    pdfUrl,
    close: closeReportDialog,
  } = useDownloadReport();
  const abortControllerJournalPoints = useRef(new AbortController());
  const abortControllerJournalVisual = useRef(new AbortController());
  const abortControllerPoints = useRef(new AbortController());
  const { getTooltip, isLoading: isTooltipLoading } = useGetTooltip({ abortControllerRef: abortControllerPoints });
  const [isLoading, setIsLoading] = useState(false);

  const [skip, setSkip] = useState<number>(0);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);
  const rawJournalResultRef = useRef<IPrepareData | null>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [columnGroupingModel, setColumnGroupingModel] = useState<GridColumnGroupingModel>([]);
  const [currentPrecision, setCurrentPrecision] = useState<Precision | null>(null);

  const prevIsVisualControlOpen = useRef<boolean | null>(null);
  const isVisualControlOpenRef = useRef<boolean>(isVisualControlOpen);
  const journalSnapshotRequestVersionRef = useRef<number>(0);
  const journalPageRequestVersionRef = useRef<number>(0);
  const requestedJournalSnapshotContextKeyRef = useRef<string>("");
  const visualDataVersionRef = useRef<number>(0);
  const visualToggleAbortControllerRef = useRef<AbortController | null>(null);
  isVisualControlOpenRef.current = isVisualControlOpen;

  const isStation = selectedItem && "Id" in selectedItem;
  const take = isStation ? STATION_JOURNAL_PAGE_SIZE : TRAIN_JOURNAL_PAGE_SIZE;
  const currentJournalStartTime =
    isRecorderMode && recorderWindowStart ? recorderWindowStart : journalCoerceIso(startTimestamp);
  const currentJournalEndTime =
    isRecorderMode && recorderWindowEnd ? recorderWindowEnd : journalCoerceIso(endTimestamp);
  const currentJournalContextKey = selectedItem
    ? getJournalContextKey(selectedItem, currentJournalStartTime, currentJournalEndTime)
    : "";
  const currentJournalSelectedItemKey = selectedItem ? getJournalSelectedItemKey(selectedItem) : "";
  const currentVisualRequestScopeKey = isRecorderMode
    ? `${currentJournalSelectedItemKey}|recorder|${userSelectedRecorderModePeriod}|${recorderModeInterval}`
    : currentJournalContextKey;
  const journalContextKeyRef = useRef<string>(currentJournalContextKey);
  const visualRequestScopeKeyRef = useRef<string>(currentVisualRequestScopeKey);
  const currentJournalStartTimeRef = useRef<string>(currentJournalStartTime);
  const currentJournalEndTimeRef = useRef<string>(currentJournalEndTime);
  journalContextKeyRef.current = currentJournalContextKey;
  visualRequestScopeKeyRef.current = currentVisualRequestScopeKey;
  currentJournalStartTimeRef.current = currentJournalStartTime;
  currentJournalEndTimeRef.current = currentJournalEndTime;

  const toggleVisual = () => {
    setIsVisualControlOpen((prev: boolean) => !prev);
  };

  const handleBack = () => {
    setJournalTimeRangeHistory((prev: Array<{ start: string; end: string }>) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const currentEnd = typeof endTimestamp === "string" ? endTimestamp : endTimestamp.toISOString();
      const isChanged = currentEnd !== last.end;
      updateTimestampsFakeUTC(last.start, last.end);

      if (!isChanged) {
        setShouldReloadJournal(true);
      }

      if (prev.length === 1) {
        setIsJournalNavigation(false);
        setManualRangeType(null);
        setSkipNextJournalReload(true);
        setJustRestoredRange(true);
      }
      return prev.slice(0, -1);
    });
  };

  const handleRestore = () => {
    if (journalTimeRangeHistory.length === 0) return;
    const first = journalTimeRangeHistory[0];
    updateTimestampsFakeUTC(first.start, first.end);
    setJournalTimeRangeHistory([]);
    setIsJournalNavigation(false);
    setManualRangeType(null);
    setSkipNextJournalReload(true);
    setJustRestoredRange(true);
  };

  const getBackTooltip = () => {
    const intervalLabel = formatMessage({ id: "pages.journals.buttons.goBackToInterval" });

    if (journalTimeRangeHistory.length === 0) {
      return intervalLabel;
    }

    const last = journalTimeRangeHistory[journalTimeRangeHistory.length - 1];
    return `${intervalLabel}: ${formatDateHumanReadable(last.start)} — ${formatDateHumanReadable(last.end)}`;
  };

  const getRestoreTooltip = () => {
    const intervalLabel = formatMessage({ id: "pages.journals.buttons.restoreInterval" });

    if (journalTimeRangeHistory.length === 0) return intervalLabel;

    const first = journalTimeRangeHistory[0];
    const startStr = formatDateHumanReadable(first.start);
    const endStr = formatDateHumanReadable(first.end);

    if (userSelectedRangeType === RangeType.SELECTED_RANGE) {
      return `${intervalLabel}: ${startStr} — ${endStr}`;
    }

    return `${intervalLabel} (${formatMessage({
      id: `enums.dateTime.${userSelectedRangeType}`,
    })}): ${startStr} — ${endStr}`;
  };

  const { fn: getJournal, isLoading: isJournalLoading } = useApiCall(
    async (
      api,
      {
        selectedItem,
        startTime,
        endTime,
        skip,
        take,
        includeVisual,
        precisionBasis,
        journalFetchSignal,
      }: IRequestJournal,
    ): Promise<IPrepareData> => {
      const selectedItemType = getSelectedItemType(selectedItem)!;
      const idTopLevel =
        ("Id" in selectedItem && selectedItem.Id) || ("TrainId" in selectedItem && selectedItem.TrainId);

      if (typeof idTopLevel !== "number") {
        throw new Error("Cannot load Journal data without a selected station or train id");
      }

      const basisStartIso = precisionBasis ? journalCoerceIso(precisionBasis.start) : startTime;
      const basisEndIso = precisionBasis ? journalCoerceIso(precisionBasis.end) : endTime;

      const precision = getPrecision({ endTimestamp: basisEndIso, startTimestamp: basisStartIso });
      updatePrecision(precision);

      const baseFetchData = {
        idTopLevel,
        startTimestamp: startTime,
        endTimestamp: endTime,
        precision,
      };

      const fetchData = {
        ...baseFetchData,
        skip,
        take,
      };

      const data: IPrepareData = { selectedItem, startTime, endTime, precision };
      const pointsSignal = journalFetchSignal ?? abortControllerJournalPoints.current.signal;
      const visualSignal = journalFetchSignal ?? abortControllerJournalVisual.current.signal;
      const { journalJson: trainsPointsData, visualJson } = await fetchJournalSnapshot(api, {
        selectedItemType,
        data: baseFetchData,
        pageData: fetchData,
        journalSignal: pointsSignal,
        signal: visualSignal,
        includeVisual: hasAdvancedFeaturesAccess && !!includeVisual,
      });

      if (selectedItemType === SELECTED_ITEM_TYPE.train) {
        data.pointsJson = trainsPointsData as IResponsePoint[];
      } else {
        data.trainsJson = trainsPointsData as IResponseTrain[];
      }

      return {
        ...data,
        visualJson: visualJson || [],
      };
    },
    { error: false, success: false },
  );

  const { fn: getJournalVisual, isLoading: isJournalVisualLoading } = useApiCall(
    async (
      api,
      { selectedItem, startTime, endTime, precisionBasis, journalFetchSignal }: IRequestJournalVisual,
    ): Promise<IJournalVisualResult> => {
      const selectedItemType = getSelectedItemType(selectedItem)!;
      const idTopLevel =
        ("Id" in selectedItem && selectedItem.Id) || ("TrainId" in selectedItem && selectedItem.TrainId);

      if (typeof idTopLevel !== "number") {
        throw new Error("Cannot load Journal visual data without a selected station or train id");
      }

      const basisStartIso = precisionBasis ? journalCoerceIso(precisionBasis.start) : startTime;
      const basisEndIso = precisionBasis ? journalCoerceIso(precisionBasis.end) : endTime;
      const precision = getPrecision({ endTimestamp: basisEndIso, startTimestamp: basisStartIso });
      const visualJson = await fetchJournalVisual(api, {
        selectedItemType,
        data: {
          idTopLevel,
          startTimestamp: startTime,
          endTimestamp: endTime,
          precision,
        },
        signal: journalFetchSignal ?? abortControllerJournalVisual.current.signal,
      });

      return {
        selectedItem,
        startTime,
        endTime,
        precision,
        visualJson,
      };
    },
    { error: false, success: false },
  );

  const getLocale = (): Locale => {
    return locales[locale.toLowerCase()];
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  const getLocaleGrid = useCallback(() => (locale === EnabledLanguages.RU ? ruRU : enUS), [locale]);

  const sortDates = (a: string, b: string) => getTime(new Date(a)) - getTime(new Date(b));

  const loadMore = useCallback(() => {
    setSkip(skip + take);
  }, [skip, take]);

  const Footer = () => {
    if (isRecorderMode || !isStation) {
      return null;
    }

    return !asuLoading && rows.length ? (
      <Box sx={{ p: 1, borderTop: "1px solid #EFEFEF" }}>
        {!isLastPage ? (
          <Button variant="outlined" fullWidth onClick={loadMore}>
            <FormattedMessage id="pages.journals.buttons.loadMore" />
          </Button>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="caption">
              <FormattedMessage id="pages.journals.captions.noRecords" />
            </Typography>
          </Box>
        )}
      </Box>
    ) : null;
  };

  const selectNewDate = (startTime: string, endTime: string): void => {
    if (userSelectedRangeType === RangeType.RECORDER_MODE) return;
    const currentStart = typeof startTimestamp === "string" ? startTimestamp : startTimestamp.toISOString();
    const currentEnd = typeof endTimestamp === "string" ? endTimestamp : endTimestamp.toISOString();

    const isChanged = currentEnd !== endTime;

    setJournalTimeRangeHistory((prev: Array<{ start: string; end: string }>) => [
      ...prev,
      {
        start: currentStart,
        end: currentEnd,
      },
    ]);
    updateTimestampsFakeUTC(startTime, endTime);
    if (!isChanged) {
      setShouldReloadJournal(true);
    }
  };

  const onRowClick = useCallback(
    async (params: GridRowParams) => {
      if (params.row?.isSectionHeader || params.row?.isJournalComments) {
        return;
      }

      if (getSelectedItemType(selectedItem) === SELECTED_ITEM_TYPE.train) {
        const trainId = selectedItem && "TrainId" in selectedItem && selectedItem.TrainId;

        if (!trainId) {
          return;
        }

        const tooltips = await getTooltip(undefined, {
          trainId,
          startTimestamp,
          endTimestamp,
        });

        if (!tooltips || tooltips.length === 0) {
          return;
        }

        const analyticsUpdateQS: any = {
          idObject: params.id,
          object: 3,
          tab: 1,
          idParameter: null,
          scrollToAnalytics: true,
        };

        analyticsUpdateQS.idParameter = tooltips[0].NameMeasure;

        updateQS(analyticsUpdateQS);

        return;
      }

      const isStationClicked = `${params.id}`.startsWith("auto-generated-row");

      if (isStationClicked) {
        const parentsNames = `${params.id}`.split("parentsNames/")[1];

        if (!parentsNames) {
          return;
        }

        const foundTrainWithParentsNames = rows.find(row => row.parentsNames === parentsNames);

        if (!foundTrainWithParentsNames || !foundTrainWithParentsNames.parentId) {
          return;
        }

        selectStation(foundTrainWithParentsNames.parentId);

        return;
      }

      selectTrain(Number(params.id));
    },
    [selectedItem, rows],
  );

  function prepareData(props: IPrepareData): void {
    const { selectedItem, trainsJson, pointsJson, visualJson, startTime, endTime, precision } = props;

    const gridRangeStartIso = startTime;
    const gridRangeEndIso = endTime;

    const trains: any = selectedItem && "Id" in selectedItem ? getAllNestedTrains(selectedItem.Id) : {};
    const dates: any = getDates({ startTime, endTime, precision });
    const points: any = {};
    const pointKeys = getJournalPointKeysInResponseOrder(pointsJson ?? []);
    const visualParams: any = {};
    const commentsRow: any = { id: "journal-comments", isJournalComments: true };

    const isVisualTrain = (item: JournalRecorderVisualItem): item is IResponseVisualTrain => {
      return "trainId" in item && "controlDate" in item;
    };

    const isVisualPoint = (item: JournalRecorderVisualItem): item is IResponseVisualPoint => {
      return "paramId" in item && "paramName" in item;
    };

    const normalizeVisualAlarmStatus = (alarmStatus: number | null | undefined): number => alarmStatus ?? 0;

    if (trainsJson) {
      trainsJson.forEach(item => {
        if (!trains[item.idTrain]) {
          return;
        }

        if (!trains[item.idTrain]) {
          const train = getTrain(item.idTrain);
          const parentsArr = train?.ParentsNames ?? [];
          const lastParent = parentsArr.length ? parentsArr[parentsArr.length - 1] : "";
          const parentsNames = `${lastParent} - ${parentsArr.join("/")}`;

          trains[item.idTrain] = {
            ...train,
            name: train?.TrainName,
            trainColour: train?.TrainColour,
            parentId: train?.ParentId,
            parentsNames,
            measures: 0,
            alarmStatus: train?.Points?.[0]?.AlarmStatus,
          };
        }

        if (!trains[item.idTrain][item.Date]) {
          trains[item.idTrain][item.Date] = `${item.AlarmStatus}/0`;
          trains[item.idTrain].measures = ((trains[item.idTrain].measures as number) || 0) + item.Count;
        }
      });
    }

    if (pointsJson) {
      pointsJson.forEach(item => {
        if (!points[item.idPoint]) {
          points[item.idPoint] = {
            name: `${item.PointName}${
              item.Direction ? ` ${formatMessage({ id: `enums.pointDirection.${item.Direction}` })}` : ""
            }`,
            measures: 0,
          };
        }

        if (!points[item.idPoint][item.Date]) {
          points[item.idPoint][item.Date] = item.AlarmStatus;
          points[item.idPoint].measures += item.Count;
        }
      });
    }

    if (pointsJson && visualJson) {
      visualJson.forEach(item => {
        if (isVisualPoint(item)) {
          if (item.paramId === -1) {
            if (item.date) {
              commentsRow[`${item.date}_comment`] = getVisualCommentTypeSymbol(item);
            }
            return;
          }

          if (!visualParams[item.paramId]) {
            visualParams[item.paramId] = {
              paramName: `${item.paramName}`,
            };
          }

          if (!item.date) {
            return;
          }

          if (visualParams[item.paramId][item.date] === undefined) {
            visualParams[item.paramId][item.date] = normalizeVisualAlarmStatus(item.alarmStatus);
          }

          visualParams[item.paramId][`${item.date}_valueType`] = item.valueType;
          visualParams[item.paramId][`${item.date}_comment`] = getVisualCommentTypeSymbol(item);
        }
      });
    }

    if (trainsJson && visualJson) {
      visualJson.forEach(item => {
        if (isVisualTrain(item)) {
          if (!trains[item.trainId]) {
            const train = getTrain(item.trainId);

            trains[item.trainId] = {
              ...train,
              measures: 0,
            };
          }
          if (!item.controlDate) {
            return;
          }

          const visualAlarmStatus = normalizeVisualAlarmStatus(item.alarmStatus);
          const existingValue = trains[item.trainId][item.controlDate] as string | undefined;
          const existingVisualStatus = existingValue ? Number(existingValue.split("/")[1] || 0) : 0;
          const nextVisualStatus = Math.max(existingVisualStatus, visualAlarmStatus);
          const pointsPart = existingValue ? existingValue.split("/")[0] : "0";

          trains[item.trainId][item.controlDate] = `${pointsPart}/${nextVisualStatus}`;
        }
      });
    }

    const columnGroups: GridColumnGroupingModel = [];
    let dataColumns: GridColDef[] = [];

    if (Object.keys(dates).length === 0) {
      setColumnGroupingModel([]);
      setColumns([]);
      setRows([]);
      return;
    }

    Object.keys(dates)
      .sort(sortDates)
      .forEach(key => {
        const groupDate = parseJournalCalendarDate(key);
        const children = dates[key]
          .sort(sortDates)
          .filter((date: string) => {
            const result =
              precision === Precision.month
                ? getTime(parseJournalCalendarDate(date)) >=
                  getTime(setDate(parseJournalCalendarDate(gridRangeStartIso), 0))
                : precision === Precision.day
                  ? getTime(setDate(groupDate, ~~date)) >= getTime(parseJournalCalendarDate(gridRangeStartIso))
                  : getTime(new Date(getJournalHourColumnField(key, date))) >=
                    getTime(new Date(toISOStringJournal(gridRangeStartIso))) - 24 * 60 * 60 * 1000;
            return result;
          })
          .map((date: string) => {
            return {
              field:
                precision === Precision.month
                  ? date
                  : precision === Precision.day
                    ? toISOStringJournal(setDate(groupDate, ~~date))
                    : getJournalHourColumnField(key, date),

              headerName:
                precision === Precision.month
                  ? format(parseJournalCalendarDate(date), "LLL", { locale: getLocale() })
                  : precision === Precision.hour
                    ? format(getJournalHourColumnDate(key, date), "HH:mm", { locale: getLocale() })
                    : date,
              minWidth: 62,
              maxWidth: 62,
              minHeight: 62,
              maxHeight: 62,
              height: 62,
              flex: 1,
              headerAlign: "center",
              align: "center",
              sortable: false,
              resizable: false,
              cellClassName: "alarm-status-cell",
              renderHeader: (params: GridColumnHeaderParams) => {
                const handleClick = (): void => {
                  if (precision === Precision.hour) {
                    return;
                  }
                  setIsJournalNavigation(true);
                  setManualRangeType(RangeType.SELECTED_RANGE);

                  const fieldDate = parseJournalCalendarDate(params.field);
                  const startTime = params.field;
                  let endTime = "";

                  if (precision === Precision.month) {
                    const daysInMonth = getDaysInMonth(fieldDate);
                    endTime = toISOStringJournal(new Date(setDate(fieldDate, daysInMonth)), { postfix: "end" });
                  }

                  if (precision === Precision.day) {
                    endTime = toISOStringJournal(fieldDate, { postfix: "end" });
                  }

                  selectNewDate(startTime, endTime);
                };

                return (
                  <Box
                    sx={{
                      cursor: "pointer",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                    onClick={handleClick}
                  >
                    {params.colDef.headerName}
                  </Box>
                );
              },
              renderCell: (params: GridRenderCellParams) => {
                if (params.row.isSectionHeader) {
                  return null;
                }

                const valueType = params.row[`${params.field}_valueType`];
                const commentTypeSymbol = params.row[`${params.field}_comment`];
                return (
                  <StyledAlarmCell
                    dateStr={params.field}
                    precision={precision}
                    status={params.value}
                    trainId={getSelectedItemType(selectedItem) === SELECTED_ITEM_TYPE.station ? params.id : undefined}
                    row={params.row}
                    onReportDownload={downloadReportByTrainId}
                    setLoading={setIsLoading}
                    commentTypeSymbol={commentTypeSymbol}
                    valueType={valueType}
                    paramId={params.row.id}
                  />
                );
              },
            };
          });

        dataColumns = dataColumns.concat(children);

        const headerName =
          precision === Precision.day
            ? format(groupDate, "LLLL yyyy", { locale: getLocale() })
            : precision === Precision.hour
              ? format(groupDate, "dd MMM yyyy", { locale: getLocale() })
              : key;

        columnGroups.push({
          groupId: toISOStringJournal(groupDate),
          headerName: capitalize(headerName),
          headerAlign: "center",
          renderHeaderGroup: (params: GridColumnGroupHeaderParams) => {
            const handleClick = (): void => {
              if (!params?.groupId) {
                return;
              }
              setIsJournalNavigation(true);
              setManualRangeType(RangeType.SELECTED_RANGE);

              const getDayRange = (date: Date): { start: string; end: string } => {
                const start = toISOStringJournal(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
                const end = toISOStringJournal(new Date(date.getFullYear(), date.getMonth(), date.getDate()), {
                  postfix: "end",
                });
                return { start, end };
              };

              const getMonthRange = (date: Date): { start: string; end: string } => {
                const start = toISOStringJournal(new Date(date.getFullYear(), date.getMonth(), 1));
                const days = getDaysInMonth(date);
                const end = toISOStringJournal(new Date(date.getFullYear(), date.getMonth(), days), { postfix: "end" });
                return { start, end };
              };

              const getYearRange = (date: Date): { start: string; end: string } => {
                const year = date.getFullYear();
                const start = toISOStringJournal(new Date(year, 0, 1));
                const end = toISOStringJournal(new Date(year, 11, 31), { postfix: "end" });
                return { start, end };
              };

              const fieldDate = parseJournalCalendarDate(params.groupId);

              if (precision === Precision.hour) {
                const isFullDay =
                  gridRangeStartIso ===
                    toISOStringJournal(new Date(fieldDate.getFullYear(), fieldDate.getMonth(), fieldDate.getDate())) &&
                  gridRangeEndIso ===
                    toISOStringJournal(new Date(fieldDate.getFullYear(), fieldDate.getMonth(), fieldDate.getDate()), {
                      postfix: "end",
                    });
                const { start, end } = isFullDay ? getMonthRange(fieldDate) : getDayRange(fieldDate);
                selectNewDate(start, end);
                return;
              }

              if (precision === Precision.day) {
                const daysInMonth = getDaysInMonth(fieldDate);
                const isFullMonth =
                  gridRangeStartIso ===
                    toISOStringJournal(new Date(fieldDate.getFullYear(), fieldDate.getMonth(), 1)) &&
                  gridRangeEndIso ===
                    toISOStringJournal(new Date(fieldDate.getFullYear(), fieldDate.getMonth(), daysInMonth), {
                      postfix: "end",
                    });

                const { start, end } = isFullMonth ? getYearRange(fieldDate) : getMonthRange(fieldDate);
                selectNewDate(start, end);
                return;
              }

              const startTime = toISOStringJournal(fieldDate);
              const endTime = toISOStringJournal(endOfYear(fieldDate), { postfix: "end" });
              selectNewDate(startTime, endTime);
            };

            return (
              <Box
                sx={{
                  cursor: "pointer",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
                onClick={handleClick}
              >
                {params.headerName}
              </Box>
            );
          },
          children,
        });
      });

    const groupingColumn = {
      field: "parentsNames",
      headerName: formatMessage({ id: "pages.journals.headers.station" }),
      flex: 2,
      minWidth: 140,
      sortable: false,
    };

    const columns: GridColDef[] = [
      ...(getSelectedItemType(selectedItem) === SELECTED_ITEM_TYPE.station ? [groupingColumn] : []),
      {
        field: "name",
        headerName: formatMessage({ id: "pages.journals.headers.objects" }),
        flex: 1,
        resizable: true,
        sortable: false,
        minWidth: 140,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box
              sx={{
                cursor: params.row.isSectionHeader ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                width: "100%",
                height: "100%",
                fontWeight: params.row.isSectionHeader ? 600 : 400,
              }}
            >
              {params.value}
            </Box>
          );
        },
        colSpan: (params: GridCellParams) => {
          if (`${params.id}`.startsWith("auto-generated-row")) {
            return columns.length - 2;
          }

          return 1;
        },
      },
      ...(getSelectedItemType(selectedItem) === SELECTED_ITEM_TYPE.train
        ? [
            {
              field: "paramName",
              headerName: formatMessage({ id: "pages.journals.headers.paramNames" }),
              flex: 1,
              resizable: true,
              sortable: false,
              minWidth: 140,
              renderCell: (params: GridRenderCellParams) => {
                return (
                  <Box
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    {params.value}
                  </Box>
                );
              },
              colSpan: (params: GridCellParams) => {
                if (`${params.id}`.startsWith("auto-generated-row")) {
                  return columns.length - 2;
                }

                return 1;
              },
            },
          ]
        : []),
      ...dataColumns,
      {
        field: "measures",
        headerName: formatMessage({ id: "pages.journals.headers.measures" }),
        flex: 1,
        minWidth: 110,
        sortable: false,
      },
    ];

    const rows: GridRowParams<any>[] =
      getSelectedItemType(selectedItem) === SELECTED_ITEM_TYPE.station
        ? Object.keys(trains).map(
            (key: string) =>
              ({
                id: key,
                ...trains[key],
              }) as GridRowParams<any>,
          )
        : [
            ...pointKeys.map(
              (key: string) =>
                ({
                  id: key,
                  ...points[key],
                }) as GridRowParams<any>,
            ),
            ...(isVisualControlOpenRef.current
              ? [
                  ...Object.keys(visualParams).map(
                    (key: string) =>
                      ({
                        id: key,
                        ...visualParams[key],
                      }) as GridRowParams<any>,
                  ),
                  {
                    id: "journal-section-comments",
                    isSectionHeader: true,
                    name: formatMessage({ id: "dialogs.comments" }),
                  },
                  commentsRow,
                ]
              : []),
          ];

    setColumnGroupingModel(columnGroups);
    setColumns(columns);
    setCurrentPrecision(precision);
    setRows(rows?.length ? rows : columnGroups.length > 0 ? [{ id: "1", [columnGroups[0].groupId]: "1" }] : []);
  }

  const commitJournalResult = (json: IPrepareData): void => {
    rawJournalResultRef.current = json;
    prepareData(json);
  };

  const beginVisualSnapshotRequest = (): number => {
    const visualVersion = ++visualDataVersionRef.current;
    visualToggleAbortControllerRef.current?.abort();
    visualToggleAbortControllerRef.current = null;
    return visualVersion;
  };

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      pinnedColumns: {
        left: ["name", "paramName"],
        right: ["measures"],
      },
      rowGrouping: {
        model: ["parentsNames"],
      },
    },
  });

  const groupingColDef = {
    colSpan: (params: GridCellParams) => {
      if (`${params.id}`.startsWith("auto-generated-row")) {
        return columns.length - 2;
      }

      return 1;
    },
  };

  useEffect(() => {
    journalRecorderAccumRef.current = null;
    journalRecorderPendingVisualDeltasRef.current = [];
    journalRecorderFetchKeyRef.current = "";
    journalRecorderHasFullJournalRef.current = false;
    journalRecorderRequestVersionRef.current += 1;
    journalSnapshotRequestVersionRef.current += 1;
    journalPageRequestVersionRef.current += 1;
  }, [currentJournalSelectedItemKey, userSelectedRecorderModePeriod, recorderModeInterval, userSelectedRangeType]);

  useEffect(() => {
    visualDataVersionRef.current += 1;
    journalRecorderPendingVisualDeltasRef.current = [];
    visualToggleAbortControllerRef.current?.abort();
    visualToggleAbortControllerRef.current = null;
  }, [currentVisualRequestScopeKey]);

  useEffect(() => {
    if (!isRecorderMode) return;
    setSkip(0);
    setIsLastPage(true);
  }, [isRecorderMode]);

  useEffect(() => {
    if (startTimestamp && endTimestamp) {
      setShouldReloadJournal(true);
    }
  }, []);

  useEffect(() => {
    if (!selectedItem || isRecorderMode) {
      requestedJournalSnapshotContextKeyRef.current = "";
      return;
    }

    const trigger = getJournalSnapshotTrigger({
      requestedContextKey: requestedJournalSnapshotContextKeyRef.current,
      currentContextKey: currentJournalContextKey,
      forceReload: shouldReloadJournal,
      isRecorderMode,
    });

    if (!trigger) {
      return;
    }

    requestedJournalSnapshotContextKeyRef.current = currentJournalContextKey;

    const requestStartTime = journalCoerceIso(startTimestamp);
    const requestEndTime = journalCoerceIso(endTimestamp);
    const requestContextKey = currentJournalContextKey;
    const requestVersion = ++journalSnapshotRequestVersionRef.current;
    journalPageRequestVersionRef.current += 1;
    const includeVisual = shouldFetchJournalVisual(hasAdvancedFeaturesAccess, isVisualControlOpen);
    const visualVersion = includeVisual ? beginVisualSnapshotRequest() : visualDataVersionRef.current;

    setIsLastPage(false);
    setSkip(0);
    void getJournal(undefined, {
      selectedItem,
      startTime: requestStartTime,
      endTime: requestEndTime,
      skip: 0,
      take,
      includeVisual,
    })
      .then(json => {
        if (
          !json ||
          requestVersion !== journalSnapshotRequestVersionRef.current ||
          requestContextKey !== journalContextKeyRef.current
        ) {
          return;
        }

        const useNextVisual = includeVisual && visualVersion === visualDataVersionRef.current;
        commitJournalResult(mergeJournalSnapshot(rawJournalResultRef.current, json, useNextVisual));
      })
      .catch(() => {});

    setShouldReloadJournal(false);
  }, [currentJournalContextKey, isRecorderMode, shouldReloadJournal]);

  useEffect(() => {
    if (prevIsVisualControlOpen.current === null) {
      prevIsVisualControlOpen.current = isVisualControlOpen;
      return;
    }

    const wasVisualOpen = prevIsVisualControlOpen.current;
    prevIsVisualControlOpen.current = isVisualControlOpen;
    const visualTransition = getJournalVisualTransition(wasVisualOpen, isVisualControlOpen);

    if (!visualTransition) {
      return;
    }

    const visualVersion = ++visualDataVersionRef.current;
    journalRecorderPendingVisualDeltasRef.current = [];
    visualToggleAbortControllerRef.current?.abort();
    visualToggleAbortControllerRef.current = null;

    if (!hasAdvancedFeaturesAccess || !selectedItem) {
      return;
    }

    if (visualTransition === "hide") {
      if (rawJournalResultRef.current) {
        prepareData(rawJournalResultRef.current);
      }
      return;
    }

    const visualStartTime =
      isRecorderMode && recorderWindowStart ? recorderWindowStart : journalCoerceIso(startTimestamp);
    const visualEndTime = isRecorderMode && recorderWindowEnd ? recorderWindowEnd : journalCoerceIso(endTimestamp);
    const precisionBasis =
      isRecorderMode && recorderWindowStart && recorderWindowEnd
        ? { start: recorderWindowStart, end: recorderWindowEnd }
        : undefined;
    const requestScopeKey = currentVisualRequestScopeKey;
    const visualAbortController = new AbortController();
    visualToggleAbortControllerRef.current = visualAbortController;

    void getJournalVisual(undefined, {
      selectedItem,
      startTime: visualStartTime,
      endTime: visualEndTime,
      ...(precisionBasis ? { precisionBasis } : {}),
      journalFetchSignal: visualAbortController.signal,
    })
      .then(json => {
        if (
          !json ||
          visualAbortController.signal.aborted ||
          visualVersion !== visualDataVersionRef.current ||
          requestScopeKey !== visualRequestScopeKeyRef.current ||
          !isVisualControlOpenRef.current
        ) {
          return;
        }

        if (visualToggleAbortControllerRef.current === visualAbortController) {
          visualToggleAbortControllerRef.current = null;
        }

        let visualJson = json.visualJson;
        let recorderResult: IPrepareData | null = null;

        if (isRecorderMode) {
          const previousSnapshot = journalRecorderAccumRef.current;
          const recorderTrains = previousSnapshot?.trainsJson ?? [];
          const recorderPoints = previousSnapshot?.pointsJson ?? [];
          const pendingVisualDeltas = journalRecorderPendingVisualDeltasRef.current;
          journalRecorderPendingVisualDeltasRef.current = [];
          const mergedVisual = applyRecorderVisualSnapshot(
            previousSnapshot?.visualJson ?? [],
            visualJson,
            pendingVisualDeltas,
            visualStartTime,
            visualEndTime,
            dbTz,
            json.precision,
          );

          journalRecorderAccumRef.current = {
            trainsJson: recorderTrains,
            pointsJson: recorderPoints,
            visualJson: mergedVisual,
          };
          const latestWindowStart = currentJournalStartTimeRef.current;
          const latestWindowEnd = currentJournalEndTimeRef.current;
          const trimmedTrains = filterJournalTrainsToWindow(
            recorderTrains,
            latestWindowStart,
            latestWindowEnd,
            dbTz,
            json.precision,
          );
          const trimmedPoints = filterJournalPointsToWindow(
            recorderPoints,
            latestWindowStart,
            latestWindowEnd,
            dbTz,
            json.precision,
          );
          visualJson = filterJournalVisualToWindow(
            mergedVisual,
            latestWindowStart,
            latestWindowEnd,
            dbTz,
            json.precision,
          );

          recorderResult = {
            ...json,
            startTime: latestWindowStart,
            endTime: latestWindowEnd,
            trainsJson: trimmedTrains,
            pointsJson: trimmedPoints,
            visualJson,
          };
        }

        const visualResult: IJournalVisualResult = {
          ...json,
          visualJson,
        };

        commitJournalResult(recorderResult ?? mergeJournalVisualResult(rawJournalResultRef.current, visualResult));
      })
      .catch(() => {})
      .finally(() => {
        if (visualToggleAbortControllerRef.current === visualAbortController) {
          visualToggleAbortControllerRef.current = null;
          journalRecorderPendingVisualDeltasRef.current = [];
        }
      });
  }, [isVisualControlOpen]);

  useEffect(() => {
    if (!isRecorderMode || !selectedItem || !recorderTickId) {
      return;
    }

    const needsFullRecorderFetch = recorderTickId === 1 || !journalRecorderHasFullJournalRef.current;

    const fetchStartIso = needsFullRecorderFetch
      ? recorderWindowStart || journalCoerceIso(startTimestamp)
      : journalCoerceIso(startTimestamp);

    const fetchEndIso = recorderWindowEnd || journalCoerceIso(endTimestamp);

    const precisionBasis =
      recorderWindowStart && recorderWindowEnd ? { start: recorderWindowStart, end: recorderWindowEnd } : undefined;
    const windowStartIso = recorderWindowStart || fetchStartIso;
    const windowEndIso = recorderWindowEnd || fetchEndIso;
    const requestContextKey = getJournalContextKey(selectedItem, windowStartIso, windowEndIso);
    const includeVisualForFetch = shouldFetchJournalVisual(hasAdvancedFeaturesAccess, isVisualControlOpenRef.current);
    const isFullVisualRequestPendingForFetch = !!visualToggleAbortControllerRef.current;
    const visualVersion = visualDataVersionRef.current;

    const selectedRecorderItemKey =
      "Id" in selectedItem ? `station-${selectedItem.Id}` : `train-${selectedItem.TrainId}`;
    const fetchKey = [
      selectedRecorderItemKey,
      recorderTickId,
      fetchStartIso,
      fetchEndIso,
      recorderWindowStart,
      recorderWindowEnd,
    ].join("|");

    if (journalRecorderFetchKeyRef.current === fetchKey) {
      return;
    }
    journalRecorderFetchKeyRef.current = fetchKey;

    const abortInflightRecorder = new AbortController();
    const requestVersion = ++journalRecorderRequestVersionRef.current;

    void getJournal(undefined, {
      selectedItem,
      startTime: fetchStartIso,
      endTime: fetchEndIso,
      skip: 0,
      take,
      includeVisual: includeVisualForFetch,
      ...(precisionBasis ? { precisionBasis } : {}),
      journalFetchSignal: abortInflightRecorder.signal,
    })
      .then(json => {
        if (
          !json ||
          requestVersion !== journalRecorderRequestVersionRef.current ||
          requestContextKey !== journalContextKeyRef.current
        ) {
          return;
        }

        const prevBucket = journalRecorderAccumRef.current;
        const trainsMerged = mergeRecorderTrains(
          prevBucket?.trainsJson ?? [],
          json.trainsJson ?? [],
          needsFullRecorderFetch,
        );
        const pointsMerged = mergeRecorderPoints(
          prevBucket?.pointsJson ?? [],
          json.pointsJson ?? [],
          needsFullRecorderFetch,
        );

        const nextVisual = json.visualJson ?? [];
        const canUseVisualResponse =
          includeVisualForFetch && visualVersion === visualDataVersionRef.current && isVisualControlOpenRef.current;
        const shouldBufferVisualResponse =
          canUseVisualResponse && isFullVisualRequestPendingForFetch && !!visualToggleAbortControllerRef.current;

        if (shouldBufferVisualResponse) {
          journalRecorderPendingVisualDeltasRef.current = mergeRecorderVisual(
            journalRecorderPendingVisualDeltasRef.current,
            nextVisual,
            false,
          );
        }

        const visualMerged = canUseVisualResponse
          ? mergeRecorderVisual(prevBucket?.visualJson ?? [], nextVisual, needsFullRecorderFetch)
          : prevBucket?.visualJson ?? [];

        journalRecorderAccumRef.current = {
          trainsJson: trainsMerged,
          pointsJson: pointsMerged,
          visualJson: visualMerged,
        };
        if (needsFullRecorderFetch) {
          journalRecorderHasFullJournalRef.current = true;
        }

        const trimmedTrains = filterJournalTrainsToWindow(
          trainsMerged,
          windowStartIso,
          windowEndIso,
          dbTz,
          json.precision,
        );
        const trimmedPoints = filterJournalPointsToWindow(
          pointsMerged,
          windowStartIso,
          windowEndIso,
          dbTz,
          json.precision,
        );
        let trimmedVisual = filterJournalVisualToWindow(
          visualMerged,
          windowStartIso,
          windowEndIso,
          dbTz,
          json.precision,
        );
        if (!isVisualControlOpenRef.current) {
          trimmedVisual = [];
        }

        const preparedJson: IPrepareData = {
          ...json,
          trainsJson: trimmedTrains,
          pointsJson: trimmedPoints,
          visualJson: trimmedVisual,
          startTime: windowStartIso,
          endTime: windowEndIso,
        };

        commitJournalResult(preparedJson);
      })
      .catch(() => {});

    return () => abortInflightRecorder.abort();
  }, [
    dbTz,
    endTimestamp,
    hasAdvancedFeaturesAccess,
    isRecorderMode,
    recorderTickId,
    recorderWindowEnd,
    recorderWindowStart,
    selectedItem,
    startTimestamp,
    take,
  ]);

  useEffect(() => {
    if (userSelectedRangeType === RangeType.RECORDER_MODE) return;
    if (!selectedItem || !skip) return;

    const requestStartTime = journalCoerceIso(startTimestamp);
    const requestEndTime = journalCoerceIso(endTimestamp);
    const requestContextKey = getJournalContextKey(selectedItem, requestStartTime, requestEndTime);
    const requestVersion = ++journalPageRequestVersionRef.current;

    void getJournal(undefined, {
      selectedItem,
      startTime: requestStartTime,
      endTime: requestEndTime,
      skip,
      take,
      includeVisual: false,
    })
      .then(json => {
        if (!json) {
          return;
        }

        if (
          requestVersion !== journalPageRequestVersionRef.current ||
          requestContextKey !== journalContextKeyRef.current
        ) {
          return;
        }

        const isStationJournal = getSelectedItemType(selectedItem) === SELECTED_ITEM_TYPE.station;
        const pageItems = isStationJournal ? json.trainsJson : json.pointsJson;

        if (!pageItems?.length) {
          setIsLastPage(true);
          return;
        }

        if (pageItems.length < take) {
          setIsLastPage(true);
        }

        commitJournalResult(mergeJournalPage(rawJournalResultRef.current, json, isStationJournal));
      })
      .catch(() => {});
  }, [skip]);

  useEffect(() => {
    return () => {
      abortControllerJournalPoints.current.abort();
      abortControllerJournalVisual.current.abort();
      visualToggleAbortControllerRef.current?.abort();
    };
  }, []);

  const loading =
    ((isJournalLoading || isJournalVisualLoading) && !isRecorderMode) ||
    (!isRecorderMode && asuLoading) ||
    isReportLoading ||
    isTooltipLoading ||
    isLoading;

  const date = new Date().toISOString().split("T")[0];

  const exportFileTitle =
    (selectedItem && "TrainName" in selectedItem && selectedItem.TrainName) ||
    (selectedItem && "Name" in selectedItem && selectedItem.Name) ||
    formatMessage({ id: "pages.dashboard.journals" });

  const exportFileName = `journal-report-${exportFileTitle}-${date}`;

  const exportCsvWithGrouping = () => {
    if (!columns || !rows) return;

    const visibleCols =
      (apiRef.current && typeof apiRef.current.getVisibleColumns === "function"
        ? apiRef.current.getVisibleColumns()
        : columns) || [];

    const exportCols = visibleCols;

    const exportGroupNames = exportCols.map(col => {
      const group = columnGroupingModel.find(g => (g.children || []).some((child: any) => child.field === col.field));
      const groupName = group?.headerName;
      return groupName;
    });
    const compactExportGroupNames = exportGroupNames.map((v, i, arr) => (i > 0 && v === arr[i - 1] ? "" : v));

    const exportHeaderNames = exportCols.map(col => {
      const colName = col.headerName || "";
      return colName;
    });

    const escapeCsv = (v: any) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };

    const csvLines: string[] = [];
    csvLines.push(compactExportGroupNames.map(g => escapeCsv(g)).join(","));
    csvLines.push(exportHeaderNames.map(h => escapeCsv(h)).join(","));

    const groupingField = "parentsNames";

    const stationColFields = new Set<string>();
    exportCols.forEach((col: any) => {
      const f = String(col.field ?? "");
      if (f === "__row_group_by_columns_group__" || col.type === "rowGroupByColumnsGroup" || f === groupingField) {
        stationColFields.add(f);
      }
    });

    if (rows && rows.length && rows.some(r => Object.prototype.hasOwnProperty.call(r, groupingField))) {
      const groups = new Map<string, any[]>();
      rows.forEach((r: any) => {
        const key = r[groupingField] ?? "";
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(r);
      });

      groups.forEach((items, groupName) => {
        const headerLine = exportCols
          .map((col: any) => {
            const f = String(col.field ?? "");
            if (stationColFields.has(f)) return escapeCsv(groupName);
            return escapeCsv("");
          })
          .join(",");
        csvLines.push(headerLine);

        items.forEach((r: any) => {
          const line = exportCols
            .map((col: any) => {
              const f = String(col.field ?? "");
              if (stationColFields.has(f)) return escapeCsv("");
              return escapeCsv(r[f]);
            })
            .join(",");
          csvLines.push(line);
        });
      });
    } else {
      rows.forEach((r: any) => {
        const line = exportCols.map((c: any) => escapeCsv(r[String(c.field ?? "")])).join(",");
        csvLines.push(line);
      });
    }

    const csvContentStr = "\uFEFF" + csvLines.join("\r\n");
    const blob = new Blob([csvContentStr], { type: "text/csv;charset=utf-8;" });
    void saveBlob(blob, `${exportFileName}.csv`, "text/csv;charset=utf-8;");
  };

  const ExportToolbar: FC = () => (
    <GridToolbarContainer
      sx={{
        justifyContent: hasAdvancedFeaturesAccess ? "space-between" : "flex-end",
        alignItems: "center",
        borderBottom: "1px solid #ebebf7",
        pb: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Tooltip title={getBackTooltip()}>
          <span>
            <IconButton onClick={handleBack} disabled={journalTimeRangeHistory.length === 0}>
              <ArrowBack />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={getRestoreTooltip()}>
          <span>
            <IconButton onClick={handleRestore} disabled={journalTimeRangeHistory.length === 0}>
              <Restore />
            </IconButton>
          </span>
        </Tooltip>

        {hasAdvancedFeaturesAccess && (
          <Tooltip
            title={
              isVisualControlOpen
                ? formatMessage({ id: "pages.journals.buttons.hideVisual" })
                : formatMessage({ id: "pages.journals.buttons.showVisual" })
            }
          >
            <IconButton onClick={toggleVisual}>{isVisualControlOpen ? <Visibility /> : <VisibilityOff />}</IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Button
          onClick={exportCsvWithGrouping}
          variant="text"
          startIcon={<FileDownloadOutlined />}
          aria-label={formatMessage({ id: "form.buttons.export" })}
          sx={{ textTransform: "uppercase", display: "inline-flex", alignItems: "center" }}
        >
          {formatMessage({ id: "form.buttons.export" })}
        </Button>
        {!isStation ? (
          <Box sx={{ ml: 1 }}>
            <InfoDialogButton title={"pages.journals.popup.titleTrain"}>
              <InformationTable />
            </InfoDialogButton>
          </Box>
        ) : null}
      </Box>
    </GridToolbarContainer>
  );

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 520,
        pointerEvents: loading ? "none" : "all",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          "& .MuiDataGrid-root": {
            border: "1px solid #ebebf7",
            borderRadius: "8px",
            color: "#363846",
            overflow: "hidden",
          },
          "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
            borderBottom: "1px solid #ebebf7",
            borderColor: "#ebebf7",
          },
          "& .MuiDataGrid-cell.alarm-status-cell": {
            minHeight: 62,
            padding: 0,
          },
          "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--filledGroup.MuiDataGrid-columnHeader--showColumnBorder":
            {
              borderColor: "#ebebf7",
            },
          "& .MuiDataGrid-pinnedColumnHeaders--right": {
            borderLeft: "1px solid #ebebf7",
          },
          "& div.MuiDataGrid-pinnedColumnHeaders": {
            backgroundColor: "#FAFAFF",
            boxShadow: "none",
          },
          "& div.MuiDataGrid-row": {
            backgroundColor: "#ffffff",
          },
          "& div.MuiDataGrid-pinnedColumns": {
            boxShadow: "none",
            borderLeft: "1px solid #ebebf7",
          },
          "& div.MuiDataGrid-cell:focus-within, & div.MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-columnSeparator:not(.MuiDataGrid-columnSeparator--resizable)": {
            display: "none",
          },
          "& .MuiDataGrid-root .MuiDataGrid-columnHeader": {
            padding: 0,
          },
          "& .MuiDataGrid-root .MuiDataGrid-columnHeaderTitleContainer": {
            borderBottom: "1px solid #ebebf7",
          },
          "& .MuiDataGrid-root .MuiDataGrid-columnHeaderTitleContainerContent": {
            width: "100%",
            padding: "0px 10px",
          },
          "& div.MuiDataGrid-row.journal-section-header, & div.MuiDataGrid-row.journal-section-header:hover": {
            backgroundColor: "#C5D9EC",
          },
          "& div.MuiDataGrid-row.journal-section-header .MuiDataGrid-cell": {
            backgroundColor: "#C5D9EC",
          },
        }}
      >
        <DataGridPremium
          key={currentPrecision ?? "initial"}
          apiRef={apiRef}
          initialState={initialState}
          columnBuffer={5}
          rowHeight={57}
          getRowHeight={params => (params.model.isSectionHeader ? 36 : 57)}
          getRowClassName={params => (params.row.isSectionHeader ? "journal-section-header" : "")}
          paginationMode="server"
          rowCount={rows?.length || 0}
          columns={rows.length ? columns : []}
          rows={rows}
          onRowClick={params => void onRowClick(params)}
          loading={false}
          components={{ Footer, Toolbar: ExportToolbar }}
          experimentalFeatures={{ columnGrouping: true }}
          localeText={getLocaleGrid().components.MuiDataGrid.defaultProps.localeText}
          columnGroupingModel={columnGroupingModel}
          defaultGroupingExpansionDepth={-1}
          rowGroupingColumnMode="single"
          groupingColDef={groupingColDef}
          disableColumnFilter
          disableColumnMenu
          disableSelectionOnClick
          disableColumnReorder
          showColumnRightBorder
          showCellRightBorder
        />
        {(isReportDialogOpen || isReportLoading) && (
          <Dialog fullWidth maxWidth="md" open={isReportDialogOpen} onClose={closeReportDialog}>
            <DialogTitle sx={{ fontWeight: 700 }}></DialogTitle>
            <DialogContent sx={{ minHeight: 500, p: 0 }}>
              {isReportLoading && (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 500 }}>
                  <CircularProgress />
                </div>
              )}
              {pdfUrl && !isReportLoading && (
                <iframe src={pdfUrl} width="100%" height="500px" style={{ border: "none" }} title="PDF Preview" />
              )}
            </DialogContent>
            <DialogActions>
              <StyledButton onClick={closeReportDialog} sx={{ minWidth: 80, mb: 1, mr: 2 }}>
                <FormattedMessage id="form.buttons.ok" />
              </StyledButton>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </Box>
  );
};
